<template>
  <div class="relative">
    <GoogleText ref="googleSearch"></GoogleText>
    <div ref="googleMap" :style="fullSize"></div>
    <MapContext
      ref="mapContext"
      :enablePointsOfInterest="enablePointsOfInterest"
      :enablePolygon="enablePolygon"
      :poiSecurity="poiSecurity"
      :polygonSecurity="polygonSecurity"
      :markerReadOnly="markerReadOnly"
      :markerPresent="isMarkerPresent"
      :recordCreatableTypes="recordCreatableTypes"
      :mapProperties="mapProperties"
      @mapMarkerEvent="addMapMarker"
      @poiMarkerAddEvent="addPoiMarker"
      @poiMarkerEditEvent="editPoiMarker"
      @poiMarkerDeleteEvent="deletePoiMarker"
      @poiMarkerDragableEvent="dragPoiMarker"
      @polygonAddEvent="addPolygonEvent"
      @polygonEditEvent="editPolygonEvent"
      @polygonDeleteEvent="deletePolygonEvent"
    ></MapContext>
  </div>
</template>

<script>
import IframeMixin from '@/mixins/IframeMixin'
import LocationMapMixin from '@/mixins/LocationMapMixin'
import LocationMapPOIMixin from '@/mixins/LocationMapPOIMixin'
import LocationMapPolygonMixin from '@/mixins/LocationMapPolygonMixin'
import GoogleText from '../components/mappings/GoogleText'
import MapContext from '../components/mappings/MapContext'

export default {
  /**
   * Name of the component
   */
  name: 'LocationMap',

  /**
   * This components mixins
   */
  mixins: [LocationMapMixin, LocationMapPOIMixin, LocationMapPolygonMixin, IframeMixin],

  /**
   * This components child components
   */
  components: {
    GoogleText,
    MapContext,
  },

  /**
   * This components properties
   */
  props: {
    latitude: String,
    longitude: String,
    readOnly: String,
    mapFeatures: Array,
    poiCreatable: String,
    poiDeletable: String,
    polygonCreatable: String,
    polygonDeletable: String,
    polygonWritable: String,
    polygonSubformFieldId: String,
    recordCreatableTypes: String,
  },

  data() {
    return {
      polygonsMap: new Map(),
    }
  },

  computed: {
    isMarkerPresent() {
      return typeof this.marker === 'object' && this.marker !== null
    },
    poiSecurity() {
      return {
        creatable: this.poiCreatable === 'true',
        deletable: this.poiDeletable === 'true',
      }
    },
    polygonSecurity() {
      return {
        creatable: this.polygonCreatable === 'true',
        deletable: this.polygonDeletable === 'true',
        writable: this.polygonWritable === 'true',
      }
    },
    markerReadOnly() {
      return this.readOnly === 'true'
    },
    enablePointsOfInterest() {
      return this.isFeatureEnabled('poi-map')
    },
    enablePolygon() {
      return this.isFeatureEnabled('polygon-map')
    },
    mapProperties() {
      return { zoom: this.zoom, mapTypeId: this.mapTypeId }
    },
  },

  /**
   * Initialize
   */
  async mounted() {
    this.centre = this.getPosition(this.latitude, this.longitude)

    await this.initializeComponent()

    this.initializeControls()
    if (this.enablePointsOfInterest) {
      this.loadPointsOfInterestTypes()
      this.initPointsOfInterest()
      this.initializePOIEventListners()
      this.relayEvent({
        event: `poi-map-loaded`,
      })
    }
    if (this.enablePolygon) {
      this.initializePolygonEventListeners()
      this.relayEvent({
        event: `polygon-map-loaded`,
      })
    }
  },

  /**
   * Component methods
   */
  methods: {
    /**
     * Initialize marker
     */
    initializeControls() {
      // position on map
      const position = this.getPosition(this.latitude, this.longitude)

      // pin
      this.setMarker(position)

      // enable zoom and map type listener
      this.map.addListener('zoom_changed', () => {
        this.relayEvent({
          event: 'zoom_changed',
          zoom: this.map.zoom,
        })
      })
      this.map.addListener('maptypeid_changed', () => {
        this.relayEvent({
          event: 'maptypeid_changed',
          mapTypeId: this.map.mapTypeId,
        })
      })

      // enable map context listeners click and right click
      this.$refs.mapContext.initializeContext(this.map)
    },

    isFeatureEnabled(feature) {
      return this.mapFeatures && this.mapFeatures.includes(feature)
    },

    addMapMarker(latLng) {
      this.addFeatureToMap(latLng, 'map')
      this.setMarker(latLng)
    },

    addPoiMarker(latLng) {
      this.addFeatureToMap(latLng, 'poi')
    },

    editPoiMarker(id) {
      this.relayEvent({
        event: `poi-marker-edit`,
        recordId: id,
      })
    },

    deletePoiMarker(id, poiMarker) {
      this.linkGlobalPOI(id)
      poiMarker.setMap(null)

      this.deletedPois.push(id)
      this.relayEvent({
        event: `poi-marker-delete`,
        recordId: id,
      })
    },

    dragPoiMarker(id, poiMarker) {
      const point = poiMarker.getPosition()
      const geocoder = new this.google.maps.Geocoder()
      geocoder.geocode({ latLng: point }, (results, status) => {
        const address = this.getFormattedAddress(results, status)
        if (address) {
          this.relayEvent({
            event: `poi-address-found`,
            address,
            dragendAddr: 'dragendAddr',
            recordId: id,
          })
        }
      })
      this.relayEvent({
        event: `poi-marker-dragged`,
        recordId: id,
        lat: point.lat(),
        lng: point.lng(),
      })
    },

    getFormattedAddress(results, status) {
      let address
      if (status === this.google.maps.GeocoderStatus.OK && results.length > 0) {
        // skip the first result if it's simply a plus code
        if (results[0].formatted_address !== results[0].plus_code?.global_code) {
          address = results[0].formatted_address
        } else if (results.length > 1) {
          address = results[1].formatted_address
        }
      }
      return address
    },

    /**
     * Marks a position on the map and relays the event
     * @param latLng position to process
     * @param eventPrefix event prefix, e.g., 'map' or 'poi'
     */
    addFeatureToMap(latLng, eventPrefix) {
      const geocoder = new this.google.maps.Geocoder()
      geocoder.geocode({ latLng }, (results, status) => {
        const address = this.getFormattedAddress(results, status)
        if (address) {
          this.relayEvent({
            event: `${eventPrefix}-address-found`,
            address,
          })
        }
      })
      this.relayEvent({
        event: `${eventPrefix}-marker-placed`,
        lat: latLng.lat(),
        lng: latLng.lng(),
      })
    },

    /**
     * Sets the position of the Pin on the map
     * @param {object} position latitude and longitude of the position
     */
    setMarker(position) {
      if (
        !Number.isNaN(position.lat) &&
        !Number.isNaN(position.lng) &&
        position.lat !== 0 &&
        position.lng !== 0
      ) {
        if (this.marker && this.marker.setMap) {
          this.marker.setMap(null)
        }
        this.marker = new this.google.maps.Marker({ position, map: this.map })
      }
    },
  },
}
</script>
